import { useState, useEffect } from "react";
import { Menu, X, ArrowRight } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_telecom-rigging/artifacts/9ue13u4p_Original%20on%20Transparent.png";

const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#services", label: "Services" },
  { href: "#experience", label: "Experience" },
  { href: "#process", label: "Process" },
  { href: "#coverage", label: "Coverage" },
  { href: "#team", label: "Team" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = ({ isScrolled, activeSection }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-[#081C3A]/90 backdrop-blur-xl border-b border-white/10 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
            : "bg-[#081C3A]/85 backdrop-blur-md border-b border-white/5 lg:bg-transparent lg:backdrop-blur-none lg:border-transparent py-4"
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          <a
            href="#home"
            onClick={(e) => scrollTo(e, "#home")}
            data-testid="logo-link"
            className="flex items-center"
          >
            <img
              src={LOGO}
              alt="Quanticom"
              className="h-14 lg:h-[72px] w-auto brightness-0 invert transition-all duration-300"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
            {navLinks.map((link) => {
              const active = activeSection === link.href.slice(1);
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollTo(e, link.href)}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                    active ? "text-[#00D4FF]" : "text-slate-200 hover:text-white"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-2 left-0 h-[2px] bg-[#00D4FF] transition-all duration-300 ${
                      active ? "w-full" : "w-0"
                    }`}
                  />
                </a>
              );
            })}
          </nav>

          <a
            href="#contact"
            onClick={(e) => scrollTo(e, "#contact")}
            className="btn-cyan hidden lg:inline-flex"
            data-testid="nav-cta-button"
          >
            Get a Quote
            <ArrowRight className="w-4 h-4" />
          </a>

          <button
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          open ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setOpen(false)}
      />
      <div
        className={`fixed top-0 right-0 z-[70] h-full w-[82%] max-w-xs bg-[#081C3A] border-l border-white/10 p-6 transition-transform duration-300 lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex items-center justify-between mb-10">
          <img src={LOGO} alt="Quanticom" className="h-12 w-auto brightness-0 invert" />
          <button
            onClick={() => setOpen(false)}
            className="p-2 text-slate-300 hover:text-white"
            data-testid="close-mobile-menu"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex flex-col gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-lg font-medium text-slate-200 hover:text-[#00D4FF] py-3 border-b border-white/5 transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          onClick={(e) => scrollTo(e, "#contact")}
          className="btn-cyan w-full mt-8"
          data-testid="mobile-cta-button"
        >
          Get a Quote <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </>
  );
};
